import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileBottomTabBar, MobileTopHeader } from "./MobileAppShell";
import { ConfiguredPageDisabledState, ConfiguredPageIntro } from "./AppConfig/ConfiguredPageState";
import { withMobileView } from "./MobileAppShell/mobileNavigation";
import { logout } from "../Common/ts/appTools";
import { AddressChangeModal } from "./ProductPhotoSelectPage/components/AddressChangeModal";
import { APP_PAGE_KEYS, getSectionProps, getVisibleSections } from "../Common/appConfig/appConfig";
import { useAppPageConfig } from "../Common/hooks/useAppConfig";
import {
  useMystudioActionItemsV4,
  useMystudioMyPageV4,
  useUpdateShippingV4,
} from "../Common/api/v4";
import { MyPhotosSection } from "./MyPhotoArchive/MyPhotoArchive";
import ActionCenter from "./ActionCenter";
import {
  createPreviewActionItems,
  formatActionDate,
  getActionDdayLabel,
  getActionPriority,
  getRemainingDays,
} from "./HomePage/customerActionItems";
import { isDemoContentAllowed } from "../Common/api/v4/previewGuard";
import MystudioDataState from "./MystudioDataState";
import "./ProductPhotoSelectPage/ProductPhotoSelectPage.css";
import "./FigmaMyPage.css";

const TEXT = {
  title: "\uB9C8\uC774\uD398\uC774\uC9C0",
  customer: "\uACE0\uAC1D\uB2D8",
  idLabel: "\uC544\uC774\uB514",
  phoneLabel: "\uC5F0\uB77D\uCC98",
  editProfile: "\uD68C\uC6D0 \uC815\uBCF4 \uC218\uC815",
  linkedStudio: "\uC5F0\uACB0 \uC2A4\uD29C\uB514\uC624",
  call: "\uC804\uD654\uD558\uAE30",
  directions: "\uAE38\uCC3E\uAE30",
  inquiry: "\uBB38\uC758\uD558\uAE30",
  contractPayment: "\uACC4\uC57D\u00B7\uACB0\uC81C",
  remainingAmount: "\uB0A8\uC740 \uAE08\uC561",
  totalContractAmount: "\uCD1D \uACC4\uC57D\uAE08\uC561",
  paidAmount: "\uACB0\uC81C \uC644\uB8CC",
  won: "\uC6D0",
  contractDoc: "\uACC4\uC57D\uC11C",
  paymentHistory: "\uACB0\uC81C\uB0B4\uC5ED",
  receipt: "\uC601\uC218\uC99D",
  shippingInfo: "\uBC30\uC1A1\uC815\uBCF4",
  edit: "\uC218\uC815\uD558\uAE30",
  accountSettings: "\uACC4\uC815 \uBC0F \uC124\uC815",
  notificationSettings: "\uC54C\uB9BC \uC124\uC815",
  marketingConsent: "\uB9C8\uCF00\uD305 \uC218\uC2E0",
  qnaHistory: "\u0031\u003A\u0031 \uBB38\uC758 \uB0B4\uC5ED",
  terms: "\uC774\uC6A9\uC57D\uAD00",
  appSettings: "\uC571 \uC124\uC815",
  logout: "\uB85C\uADF8\uC544\uC6C3",
  activity: "\uB098\uC758 \uD65C\uB3D9",
  todayTasks: "\uC624\uB298 \uD560 \uC77C",
  home: "\uD648",
  schedule: "\uCD2C\uC601 \uC77C\uC815",
  product: "\uC81C\uD488 \uC2E0\uCCAD",
  theme: "\uCD2C\uC601 \uD14C\uB9C8",
  mypage: "\uB9C8\uC774\uD398\uC774\uC9C0",
};

const ASSETS = {
  account: "account",
  phone: "phone",
  store: "store",
  clock: "clock",
  studioPhone: "phone",
  callPhone: "call",
  map: "map",
  message: "message",
  taskDone: "checklist",
  task: "document",
  creditCard: "card",
  invoice: "receipt",
  truck: "truck",
  chevron: "chevron",
  settingsBell: "bell",
  settingsMegaphone: "megaphone",
  settingsMessage: "message",
  settingsTask: "document",
  settingsChevron: "chevron",
  settingsGear: "gear",
};

const EMPTY_SHIPPING = {
  title: "배송지",
  receiver: "",
  recipient: "",
  phone: "",
  address: "",
  postcode: "",
  addressLine1: "",
  addressLine2: "",
  detailAddress: "",
};

const ACTION_PRESENTATION = {
  original: {
    type: "original_download",
    description: "보관 기간이 끝나기 전에 원본사진을 내려받아 주세요.",
    label: "원본 확인",
    path: "/mypage/photos?from=mypage-today-tasks",
  },
  theme: {
    type: "theme_selection",
    description: "촬영 전에 원하는 테마를 선택해 주세요.",
    label: "테마 선택",
    path: "/tabs/themes?from=mypage-today-tasks",
  },
  product: {
    type: "product_photo_selection",
    description: "제품 제작에 사용할 사진을 선택해 주세요.",
    label: "사진 선택",
    path: "/tabs/products?from=mypage-today-tasks",
  },
  schedule: {
    type: "schedule_guide",
    description: "촬영 전에 일정과 준비 안내를 확인해 주세요.",
    label: "일정 확인",
    path: "/tabs/schedules?from=mypage-today-tasks",
  },
};

function getActionPresentation(code) {
  const normalizedCode = String(code || "").trim().toUpperCase();
  if (normalizedCode.includes("ORIGINAL") || normalizedCode.includes("DOWNLOAD")) {
    return ACTION_PRESENTATION.original;
  }
  if (normalizedCode.includes("THEME") || normalizedCode.includes("THEMA")) {
    return ACTION_PRESENTATION.theme;
  }
  if (normalizedCode.includes("PRODUCT") || normalizedCode.includes("PHOTO_SELECT")) {
    return ACTION_PRESENTATION.product;
  }
  return ACTION_PRESENTATION.schedule;
}

function buildMyPageActionItems(items, now = new Date()) {
  return (Array.isArray(items) ? items : []).map((item, index) => {
    const presentation = getActionPresentation(item?.code);
    const dueAt = item?.dueDate || null;
    const remainingDays = getRemainingDays(dueAt, now);
    const { priority, priorityRank } = getActionPriority(remainingDays);
    const deadlineDate = formatActionDate(dueAt);
    const scheduleQuery = item?.scheduleId
      ? `${presentation.path.includes("?") ? "&" : "?"}scheduleId=${encodeURIComponent(item.scheduleId)}`
      : "";

    return {
      id: `v4:${item?.code || "action"}:${item?.scheduleId || index}`,
      type: presentation.type,
      title: item?.title || "확인이 필요한 항목",
      description: presentation.description,
      dueAt,
      deadlineLabel: deadlineDate ? `${deadlineDate}까지` : "",
      remainingDays,
      ddayLabel: getActionDdayLabel(remainingDays),
      priority,
      priorityRank,
      cta: {
        label: presentation.label,
        path: `${presentation.path}${scheduleQuery}`,
      },
      canSnooze: false,
      sourceKind: "api",
    };
  });
}

function maskValue(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= 2) return `${text[0] || ""}*`;
  return `${text.slice(0, 3)}***${text.slice(-2)}`;
}

function maskPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length < 8) return "정보 없음";
  return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
}

function navigateTo(target) {
  window.location.href = withMobileView(target);
}

function openExternal(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

function buildShippingAddress(shippingInfo) {
  const addressLine1 = shippingInfo.addressLine1 || shippingInfo.baseAddress || shippingInfo.address || "";
  const addressLine2 = shippingInfo.addressLine2 || shippingInfo.detailAddress || shippingInfo.detail || "";
  return [addressLine1, addressLine2].filter(Boolean).join(" ").trim();
}

function normalizeShippingInfo(shippingInfo) {
  const fallback = EMPTY_SHIPPING;
  const nextAddress = buildShippingAddress(shippingInfo) || fallback.address;

  return {
    ...fallback,
    ...shippingInfo,
    recipient: shippingInfo.recipient || shippingInfo.receiver || fallback.recipient,
    receiver: shippingInfo.recipient || shippingInfo.receiver || fallback.receiver,
    phone: shippingInfo.phone || fallback.phone,
    address: nextAddress,
    detailAddress: shippingInfo.addressLine2 || shippingInfo.detailAddress || shippingInfo.detail || "",
    addressLine1: shippingInfo.addressLine1 || shippingInfo.baseAddress || shippingInfo.address || fallback.addressLine1,
    addressLine2: shippingInfo.addressLine2 || shippingInfo.detailAddress || shippingInfo.detail || "",
    postcode: shippingInfo.postcode || "",
  };
}

function MobileStatusBar() {
  return (
    <div className="figma-mypage-status" aria-hidden="true">
      <span>9:41</span>
      <span className="figma-mypage-status__icons">
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

function MyPageIcon({ name, className = "" }) {
  const iconClassName = ["figma-mypage-svg-icon", className].filter(Boolean).join(" ");

  if (name === "account") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 12.25a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
        <path d="M5.75 19.25a6.25 6.25 0 0 1 12.5 0" />
      </svg>
    );
  }

  if (name === "phone" || name === "call") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M7.25 5.5 9 4.75l2.1 4.25-1.55 1.15a9.5 9.5 0 0 0 4.3 4.3L15 12.9l4.25 2.1-.75 1.75c-.35.8-1.12 1.35-1.98 1.25C10.3 17.35 6.65 13.7 6 7.48c-.1-.86.45-1.63 1.25-1.98Z" />
      </svg>
    );
  }

  if (name === "store") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M5.5 10.5v7.25c0 .83.67 1.5 1.5 1.5h10c.83 0 1.5-.67 1.5-1.5V10.5" />
        <path d="M4.75 9.75h14.5l-1.35-5H6.1l-1.35 5Z" />
        <path d="M8.75 9.75v1a2 2 0 1 0 4 0v-1" />
        <path d="M12.75 9.75v1a2 2 0 1 0 4 0v-1" />
        <path d="M9.25 19.25v-4.5h5.5v4.5" />
      </svg>
    );
  }

  if (name === "clock") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 5.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Z" />
        <path d="M12 8.75v3.75l2.55 1.55" />
      </svg>
    );
  }

  if (name === "map") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 20.25s5.25-4.48 5.25-9.25a5.25 5.25 0 0 0-10.5 0c0 4.77 5.25 9.25 5.25 9.25Z" />
        <path d="M12 12.75a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5Z" />
      </svg>
    );
  }

  if (name === "message") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M5.75 6.75A2.75 2.75 0 0 1 8.5 4h7A2.75 2.75 0 0 1 18.25 6.75v5.5A2.75 2.75 0 0 1 15.5 15h-4.75L7 18.25V15.1a2.75 2.75 0 0 1-1.25-2.3V6.75Z" />
        <path d="M9 9.5h6" />
        <path d="M9 12h4" />
      </svg>
    );
  }

  if (name === "checklist" || name === "document") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M8 5.5h7.5A2.5 2.5 0 0 1 18 8v8.5a2.5 2.5 0 0 1-2.5 2.5H8a2.5 2.5 0 0 1-2.5-2.5V8A2.5 2.5 0 0 1 8 5.5Z" />
        <path d="M8.75 10h5.5" />
        <path d="M8.75 13h4.25" />
        {name === "checklist" ? <path d="m14.75 15.2 1.05 1.05 2-2.25" /> : null}
      </svg>
    );
  }

  if (name === "card") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M5.25 7.75A2.25 2.25 0 0 1 7.5 5.5h9a2.25 2.25 0 0 1 2.25 2.25v8.5a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25v-8.5Z" />
        <path d="M5.5 9.75h13" />
        <path d="M8.25 14.75h3" />
      </svg>
    );
  }

  if (name === "receipt") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M7 4.75h10v14.5l-2-1.2-2 1.2-2-1.2-2 1.2-2-1.2V4.75Z" />
        <path d="M9.5 9h5" />
        <path d="M9.5 12h5" />
        <path d="M9.5 15h3" />
      </svg>
    );
  }

  if (name === "truck") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M4.75 6.75h9.5v8.5h-9.5v-8.5Z" />
        <path d="M14.25 9.25h2.75l2.25 2.75v3.25h-5" />
        <path d="M7.75 18.25a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5Z" />
        <path d="M16.75 18.25a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5Z" />
      </svg>
    );
  }

  if (name === "bell") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M7.25 16.25h9.5l-1-1.45V11a3.75 3.75 0 1 0-7.5 0v3.8l-1 1.45Z" />
        <path d="M10.5 18.25h3" />
      </svg>
    );
  }

  if (name === "megaphone") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M5.25 13.25h2.5l7.5 3.5v-9.5l-7.5 3.5h-2.5v2.5Z" />
        <path d="M7.75 13.25v3.25a1.75 1.75 0 0 0 1.75 1.75h.75" />
        <path d="M17.25 10.25a2.75 2.75 0 0 1 0 3.5" />
      </svg>
    );
  }

  if (name === "gear") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 14.75a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5Z" />
        <path d="M18.2 13.25v-2.5l-1.75-.35a5.2 5.2 0 0 0-.55-1.3l1-1.5-1.8-1.8-1.5 1a5.2 5.2 0 0 0-1.35-.55L11.9 4.5H9.4l-.35 1.75a5.2 5.2 0 0 0-1.3.55l-1.5-1-1.8 1.8 1 1.5c-.25.42-.43.85-.55 1.3l-1.75.35v2.5l1.75.35c.12.45.3.88.55 1.3l-1 1.5 1.8 1.8 1.5-1c.42.25.85.43 1.3.55l.35 1.75h2.5l.35-1.75c.47-.12.92-.3 1.35-.55l1.5 1 1.8-1.8-1-1.5c.25-.42.43-.85.55-1.3l1.75-.35Z" />
      </svg>
    );
  }

  if (name === "calendar") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M6.5 5.75h11A1.75 1.75 0 0 1 19.25 7.5v10A1.75 1.75 0 0 1 17.5 19.25h-11A1.75 1.75 0 0 1 4.75 17.5v-10A1.75 1.75 0 0 1 6.5 5.75Z" />
        <path d="M8.25 4.25v3" />
        <path d="M15.75 4.25v3" />
        <path d="M4.75 9.25h14.5" />
      </svg>
    );
  }

  if (name === "product") {
    return (
      <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M6.25 8.5h11.5l-1 10H7.25l-1-10Z" />
        <path d="M9 8.5a3 3 0 1 1 6 0" />
      </svg>
    );
  }

  return (
    <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function ProfileAvatar() {
  return (
    <span className="figma-mypage-profile__avatar" data-node-id="6859:2125" aria-hidden="true">
      <MyPageIcon name={ASSETS.account} />
    </span>
  );
}

function ProfileMetaRow({ icon, label, value }) {
  return (
    <div className="figma-mypage-profile__meta-row">
      <span className="figma-mypage-profile__meta-label">
        <MyPageIcon name={icon} className="figma-mypage-profile__meta-icon" />
        {label}
      </span>
      <span className="figma-mypage-profile__meta-value">{value}</span>
    </div>
  );
}

function ProfileCard({ user, title = "", description = "", onEdit }) {
  return (
    <section className="figma-mypage-profile" data-node-id="6743:17096">
      {title || description ? (
        <div className="figma-mypage-config-heading">
          {title ? <h2>{title}</h2> : null}
          {description ? <p>{description}</p> : null}
        </div>
      ) : null}
      <div className="figma-mypage-profile__top" data-node-id="6957:5759">
        <ProfileAvatar />
        <div className="figma-mypage-profile__info" data-node-id="6743:17100">
          <div className="figma-mypage-profile__name-row">
            <strong data-node-id="6743:17102">{user.name}</strong>
            <span data-node-id="6957:5742">{TEXT.customer}</span>
          </div>
          <div className="figma-mypage-profile__meta" data-node-id="6975:6113">
            <ProfileMetaRow icon={ASSETS.account} label={TEXT.idLabel} value={user.id} />
            <ProfileMetaRow icon={ASSETS.phone} label={TEXT.phoneLabel} value={user.phone} />
          </div>
        </div>
      </div>
      <button className="figma-mypage-profile__edit" type="button" data-node-id="6957:5794" onClick={onEdit}>
        {TEXT.editProfile}
      </button>
    </section>
  );
}

function PageSection({ title, description = "", nodeId, titleNodeId, className = "", children }) {
  return (
    <section className={["figma-mypage-section", className].filter(Boolean).join(" ")} data-node-id={nodeId}>
      <div className="figma-mypage-section__title-wrap">
        <h2 className="figma-mypage-section__title" data-node-id={titleNodeId}>{title}</h2>
        {description ? <p className="figma-mypage-section__description">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function IconBubble({ src, nodeId }) {
  return (
    <span className="figma-mypage-icon-bubble" data-node-id={nodeId} aria-hidden="true">
      <MyPageIcon name={src} />
    </span>
  );
}

function TodayTasksSection({
  items,
  isCountKnown,
  title = TEXT.activity,
  description = "",
  onNavigate,
}) {
  const safeItems = Array.isArray(items) ? items : [];
  const safeCount = safeItems.length;
  const taskHeading = String(title || "").trim() === TEXT.todayTasks
    ? "진행 중인 항목"
    : TEXT.todayTasks;
  const accessibleLabel = isCountKnown
    ? `${TEXT.todayTasks} ${safeCount}\uAC1C, \uD655\uC778\uD558\uAE30`
    : `${TEXT.todayTasks} \uD655\uC778\uD558\uAE30`;
  const handleAction = (item) => {
    const target = item?.cta?.path || item?.href || "/tabs/home?screen=today-tasks";
    onNavigate(target);
  };

  return (
    <PageSection title={title} description={description} className="figma-mypage-activity-section">
      <div className="figma-mypage-tasks-heading">
        <div>
          <span className="figma-mypage-tasks-heading__eyebrow">TODAY</span>
          <h3 id="figma-mypage-today-tasks-title">{taskHeading}</h3>
        </div>
        <span className={`figma-mypage-activity-entry__count${isCountKnown ? "" : " is-unknown"}`}>
          <span className="figma-mypage-sr-only">{isCountKnown ? "해야 할 일" : "할 일 수"}</span>
          {isCountKnown ? safeCount : "확인 중"}
        </span>
      </div>

      {!isCountKnown ? (
        <div className="figma-mypage-tasks-state" role="status">
          <span className="figma-mypage-tasks-state__icon" aria-hidden="true">
            <MyPageIcon name={ASSETS.clock} />
          </span>
          <span>
            <strong>할 일을 확인하고 있어요</strong>
            <small>잠시만 기다려 주세요.</small>
          </span>
        </div>
      ) : safeCount > 0 ? (
        <ActionCenter
          className="figma-mypage-tasks-list"
          items={safeItems}
          onAction={handleAction}
          defaultExpanded
          showHeader={false}
          labelledBy="figma-mypage-today-tasks-title"
        />
      ) : (
        <div className="figma-mypage-tasks-state figma-mypage-tasks-state--complete" role="status">
          <span className="figma-mypage-tasks-state__icon" aria-hidden="true">
            <MyPageIcon name={ASSETS.taskDone} />
          </span>
          <span>
            <strong>오늘 할 일을 모두 마쳤어요</strong>
            <small>새로운 할 일이 생기면 이곳에서 알려드릴게요.</small>
          </span>
        </div>
      )}

      <button
        className="figma-mypage-tasks-all"
        type="button"
        aria-label={accessibleLabel}
        onClick={() => onNavigate("/tabs/home?screen=today-tasks")}
      >
        <span>할 일 전체보기</span>
        <MyPageIcon name={ASSETS.chevron} />
      </button>
    </PageSection>
  );
}

function InlineIcon({ src, className = "" }) {
  return <MyPageIcon name={src} className={`figma-mypage-inline-icon ${className}`} />;
}

function ClockIcon() {
  return <InlineIcon src={ASSETS.clock} className="figma-mypage-inline-icon--clock" />;
}

function ActionButton({ icon, label, onClick, nodeId, iconSize = "small", disabled = false }) {
  return (
    <button
      className="figma-mypage-action-button"
      type="button"
      onClick={onClick}
      data-node-id={nodeId}
      disabled={disabled}
    >
      <InlineIcon src={icon} className={`figma-mypage-inline-icon--${iconSize}`} />
      <span>{label}</span>
    </button>
  );
}

function StudioInfoSection({ studio, title = TEXT.linkedStudio, description = "" }) {
  if (!studio) {
    return (
      <PageSection title={title} description={description} nodeId="6975:9823" titleNodeId="6975:9825">
        <div className="figma-mypage-card figma-mypage-studio-card">
          <p className="figma-mypage-empty-state">연결된 스튜디오 정보가 없어요.</p>
        </div>
      </PageSection>
    );
  }

  const hasPhone = Boolean(studio.phone);

  return (
    <PageSection title={title} description={description} nodeId="6975:9823" titleNodeId="6975:9825">
      <div className="figma-mypage-card figma-mypage-studio-card" data-node-id="6975:9826">
        <div className="figma-mypage-card__body">
          <div className="figma-mypage-card__heading-row" data-node-id="6975:9828">
            <IconBubble src={ASSETS.store} nodeId="6975:9887" />
            <strong className="figma-mypage-card__heading" data-node-id="6975:9830">{studio.name}</strong>
          </div>
          <div className="figma-mypage-studio-meta" data-node-id="6975:9831">
            <div className="figma-mypage-studio-meta__item" data-node-id="6975:9832">
              <ClockIcon />
              <span>{studio.hours}</span>
            </div>
            <div className="figma-mypage-studio-meta__item" data-node-id="6975:9835">
              <InlineIcon src={ASSETS.studioPhone} />
              <span>{studio.phone || "연락처 정보 없음"}</span>
            </div>
          </div>
        </div>
        <div className="figma-mypage-action-row" data-node-id="6975:9838">
          <ActionButton
            icon={ASSETS.callPhone}
            label={hasPhone ? TEXT.call : "연락처 없음"}
            nodeId="6975:9839"
            disabled={!hasPhone}
            onClick={() => {
              if (hasPhone) window.location.href = `tel:${studio.phone}`;
            }}
          />
          <ActionButton icon={ASSETS.map} label={TEXT.directions} nodeId="6975:9843" onClick={() => openExternal(`https://map.kakao.com/link/search/${encodeURIComponent(studio.mapQuery)}`)} />
          <ActionButton icon={ASSETS.message} label={TEXT.inquiry} nodeId="6975:9847" iconSize="large" onClick={() => navigateTo("/qnaList?screen=inquiry")} />
        </div>
      </div>
    </PageSection>
  );
}

function AmountLine({ label, amount, emphasis, unit = TEXT.won }) {
  return (
    <div className="figma-mypage-amount-line">
      <span>{label}</span>
      <strong className={emphasis ? "figma-mypage-amount-line__value figma-mypage-amount-line__value--large" : "figma-mypage-amount-line__value"}>
        <span>{amount}</span>
        {unit ? <small>{unit}</small> : null}
      </strong>
    </div>
  );
}

function ContractPaymentSection({
  contract,
  title = TEXT.contractPayment,
  description = "",
}) {
  if (!contract) {
    return (
      <PageSection title={title} description={description} nodeId="6957:5700" titleNodeId="6957:5702">
        <div className="figma-mypage-card figma-mypage-contract-card">
          <p className="figma-mypage-empty-state">결제 내역 없음</p>
        </div>
      </PageSection>
    );
  }

  const amountUnit = contract.amountUnit ?? TEXT.won;

  return (
    <PageSection title={title} description={description} nodeId="6957:5700" titleNodeId="6957:5702">
      <div className="figma-mypage-card figma-mypage-contract-card" data-node-id="6957:5703">
        <div className="figma-mypage-contract-content" data-node-id="6957:5708">
          <div className="figma-mypage-card__heading-row figma-mypage-contract-heading" data-node-id="6975:5888">
            <div className="figma-mypage-card__heading-left" data-node-id="6975:5892">
              <IconBubble src={ASSETS.taskDone} nodeId="6975:9907" />
              <strong className="figma-mypage-card__heading" data-node-id="6957:5709">{contract.title}</strong>
            </div>
            <span className="figma-mypage-status-badge" data-node-id="6975:5889">{contract.status}</span>
          </div>
          <div className="figma-mypage-contract-amounts" data-node-id="6975:5893">
            <div className="figma-mypage-contract-remaining" data-node-id="6957:5863">
              <span>{TEXT.remainingAmount}</span>
              <strong>
                <span>{contract.remainingAmount}</span>
                {amountUnit ? <small>{amountUnit}</small> : null}
              </strong>
            </div>
            <div className="figma-mypage-contract-summary" data-node-id="6957:5710">
              <AmountLine label={TEXT.totalContractAmount} amount={contract.totalAmount} unit={amountUnit} />
              <AmountLine label={TEXT.paidAmount} amount={contract.paidAmount} unit={amountUnit} />
            </div>
          </div>
        </div>
        <div className="figma-mypage-action-row" data-node-id="6975:5894">
          <ActionButton icon={ASSETS.task} label={TEXT.contractDoc} nodeId="6975:5895" onClick={() => navigateTo("/mypage/contract-docs")} />
          <ActionButton icon={ASSETS.creditCard} label={TEXT.paymentHistory} nodeId="6975:5899" onClick={() => navigateTo("/mypage/payment-history")} />
          <ActionButton icon={ASSETS.invoice} label={TEXT.receipt} nodeId="6975:5903" onClick={() => navigateTo("/mypage/receipts")} />
        </div>
      </div>
    </PageSection>
  );
}

function ShippingInfoSection({ shipping, delivery, title = TEXT.shippingInfo, description = "", onEdit }) {
  if (!shipping) {
    return (
      <PageSection title={title} description={description} nodeId="6957:5561" titleNodeId="6957:5563">
        <div className="figma-mypage-card figma-mypage-shipping-card">
          <p className="figma-mypage-empty-state">배송지 미등록</p>
          {onEdit ? (
            <button className="figma-mypage-shipping-edit" type="button" onClick={onEdit}>
              배송지 등록 요청
            </button>
          ) : null}
        </div>
      </PageSection>
    );
  }

  return (
    <PageSection title={title} description={description} nodeId="6957:5561" titleNodeId="6957:5563">
      <div className="figma-mypage-card figma-mypage-shipping-card" data-node-id="6957:5612">
        <div className="figma-mypage-shipping-head" data-node-id="6957:5669">
          <div className="figma-mypage-card__heading-left" data-node-id="6975:9873">
            <IconBubble src={ASSETS.truck} nodeId="6975:9946" />
            <strong className="figma-mypage-card__heading" data-node-id="6975:9871">{shipping.title}</strong>
          </div>
          {onEdit ? (
            <button className="figma-mypage-shipping-edit" type="button" onClick={onEdit} data-node-id="6957:5737">
              <span>{TEXT.edit}</span>
              <MyPageIcon name={ASSETS.chevron} className="figma-mypage-settings-chevron" />
            </button>
          ) : null}
        </div>
        <div className="figma-mypage-shipping-body" data-node-id="6957:5622">
          <strong data-node-id="6957:5621">{shipping.receiver}</strong>
          <span data-node-id="6975:9951">{shipping.phone}</span>
          <p data-node-id="6957:5614">{shipping.address}</p>
          {delivery?.availability === "available" ? (
            <p className="figma-mypage-shipping-delivery">
              <b>{delivery.status || "배송 등록"}</b>
              {delivery.deliveryType ? <span>{delivery.deliveryType}</span> : null}
              {delivery.trackingNumber ? <span>송장 {delivery.trackingNumber}</span> : null}
            </p>
          ) : null}
        </div>
      </div>
    </PageSection>
  );
}

function SettingsRow({ icon, label, nodeId, children, onClick }) {
  const Component = onClick ? "button" : "div";

  return (
    <Component className="figma-mypage-settings-row" type={onClick ? "button" : undefined} data-node-id={nodeId} onClick={onClick}>
      <InlineIcon src={icon} className="figma-mypage-inline-icon--setting" />
      <span className="figma-mypage-settings-row__label">{label}</span>
      {children}
    </Component>
  );
}

function SettingsChevron() {
  return <MyPageIcon name={ASSETS.settingsChevron} className="figma-mypage-settings-chevron" />;
}

function QuickActionCard({ icon, count, label, helper, onClick, tone = "blue" }) {
  return (
    <button className={`figma-mypage-quick-card is-${tone}`} type="button" onClick={onClick}>
      <span className="figma-mypage-quick-card__icon" aria-hidden="true">
        <MyPageIcon name={icon} />
      </span>
      <span className="figma-mypage-quick-card__text">
        <strong>{count}</strong>
        <span>{label}</span>
        <small>{helper}</small>
      </span>
    </button>
  );
}

function QuickActionSummary({ onNavigate }) {
  return (
    <section className="figma-mypage-quick-summary" aria-label={"\uB9C8\uC774\uD398\uC774\uC9C0 \uBC14\uB85C\uAC00\uAE30"}>
      <QuickActionCard
        icon="calendar"
        count="2"
        label={TEXT.schedule}
        helper={"\uB2E4\uAC00\uC624\uB294 \uC608\uC57D"}
        onClick={() => onNavigate("/tabs/schedules?category=upcoming")}
      />
      <QuickActionCard
        icon="product"
        count="3"
        label={TEXT.product}
        helper={"\uC0AC\uC9C4 \uC120\uD0DD \uD544\uC694"}
        tone="mint"
        onClick={() => onNavigate("/productList?tab1=1")}
      />
      <QuickActionCard
        icon="message"
        count="1"
        label={"\u0031\u003A\u0031 \uBB38\uC758"}
        helper={"\uB2F5\uBCC0 \uD655\uC778"}
        tone="violet"
        onClick={() => onNavigate("/qnaList?screen=inquiry")}
      />
    </section>
  );
}

function AccountSettingsSection({
  title = TEXT.accountSettings,
  description = "",
  onLogout,
  onNavigate,
}) {
  const handleLogout = () => {
    onLogout();
  };

  return (
    <section className="figma-mypage-settings" data-node-id="6743:17031">
      <div className="figma-mypage-settings__main" data-node-id="6743:17032">
        <div className="figma-mypage-settings__title-wrap" data-node-id="6743:17033">
          <h2 className="figma-mypage-settings__title" data-node-id="6743:17034">{title}</h2>
          {description ? <p className="figma-mypage-section__description">{description}</p> : null}
        </div>
        <div className="figma-mypage-settings-list" data-node-id="6743:17035">
          <SettingsRow icon={ASSETS.settingsBell} label={TEXT.notificationSettings} nodeId="6743:17068" onClick={() => onNavigate("/mypage/app-settings")}>
            <SettingsChevron />
          </SettingsRow>
          <SettingsRow icon={ASSETS.settingsMegaphone} label={TEXT.marketingConsent} nodeId="6743:17075" onClick={() => onNavigate("/mypage/app-settings")}>
            <SettingsChevron />
          </SettingsRow>
          <SettingsRow icon={ASSETS.settingsMessage} label={TEXT.qnaHistory} nodeId="6743:17048" onClick={() => navigateTo("/qnaList?screen=inquiry")}>
            <SettingsChevron />
          </SettingsRow>
          <SettingsRow icon={ASSETS.settingsGear} label={TEXT.appSettings} nodeId="7038:15607" onClick={() => onNavigate("/mypage/app-settings")}>
            <SettingsChevron />
          </SettingsRow>
          <SettingsRow icon={ASSETS.settingsTask} label={TEXT.terms} nodeId="6743:17044" onClick={() => onNavigate("/mypage/terms")}>
            <SettingsChevron />
          </SettingsRow>
        </div>
      </div>
      <div className="figma-mypage-logout-wrap" data-node-id="6743:17089">
        <button className="figma-mypage-logout" type="button" data-node-id="6743:17090" onClick={handleLogout} aria-label="로그아웃 후 로그인 화면으로 이동">
          {TEXT.logout}
        </button>
      </div>
    </section>
  );
}

function formatAmount(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toLocaleString("ko-KR") : "0";
}

function buildV4User(profile) {
  return {
    rawName: profile?.displayName || "",
    name: profile?.displayName || "이름 정보 없음",
    id: profile?.memberId ? maskValue(profile.memberId) : "정보 없음",
    phone: profile?.phone ? maskPhone(profile.phone) : "정보 없음",
  };
}

function buildV4Studio(profile) {
  if (!profile?.office) return null;
  return {
    name: profile.office.name,
    hours: profile.office.hours || "운영시간 정보 없음",
    phone: profile.office.phone || "",
    mapQuery: profile.office.address || profile.office.name,
  };
}

function buildV4Contract(profile) {
  const contract = profile?.contracts?.[0];
  if (!contract) return null;
  return {
    title: contract.title,
    status: contract.status,
    remainingAmount: formatAmount(contract.remainingAmount),
    totalAmount: formatAmount(contract.totalAmount),
    paidAmount: formatAmount(contract.paidAmount),
  };
}

function buildV4Shipping(profile) {
  if (!profile?.shippingAddress) return null;
  const shipping = profile.shippingAddress;
  return normalizeShippingInfo({
    id: shipping.id,
    title: "기본배송지",
    receiver: shipping.recipient,
    recipient: shipping.recipient,
    phone: shipping.phone,
    postcode: shipping.postcode || "",
    addressLine1: shipping.addressLine1,
    addressLine2: shipping.addressLine2 || "",
    detailAddress: shipping.addressLine2 || "",
    address: [shipping.addressLine1, shipping.addressLine2].filter(Boolean).join(" "),
  });
}

export default function FigmaMyPage() {
  const routerNavigate = useNavigate();
  const myPageQuery = useMystudioMyPageV4();
  const actionItemsQuery = useMystudioActionItemsV4();
  const shippingMutation = useUpdateShippingV4();
  const v4Profile = myPageQuery.data?.data || null;
  const user = useMemo(() => buildV4User(v4Profile), [v4Profile]);
  const [shipping, setShipping] = useState({ ...EMPTY_SHIPPING });
  const [isAddressEditorOpen, setIsAddressEditorOpen] = useState(false);
  const [shippingError, setShippingError] = useState("");
  const [shippingSuccess, setShippingSuccess] = useState("");
  const { pageConfig: myPageConfig } = useAppPageConfig(APP_PAGE_KEYS.mypage);
  const visibleMyPageSections = getVisibleSections(myPageConfig);
  const resolvedStudio = useMemo(
    () => v4Profile ? buildV4Studio(v4Profile) : null,
    [v4Profile],
  );
  const resolvedContract = useMemo(
    () => v4Profile ? buildV4Contract(v4Profile) : null,
    [v4Profile],
  );
  const resolvedShipping = useMemo(
    () => v4Profile ? buildV4Shipping(v4Profile) : null,
    [v4Profile],
  );
  const resolvedShippingKey = JSON.stringify(resolvedShipping || EMPTY_SHIPPING);
  const actionSummary = useMemo(() => {
    const apiItems = buildMyPageActionItems(actionItemsQuery.data?.data);
    const usePreviewItems = isDemoContentAllowed();
    return {
      items: usePreviewItems ? createPreviewActionItems() : apiItems,
      isKnown: usePreviewItems || (
        actionItemsQuery.configured
        && !actionItemsQuery.isLoading
        && !actionItemsQuery.error
      ),
    };
  }, [
    actionItemsQuery.configured,
    actionItemsQuery.data?.data,
    actionItemsQuery.error,
    actionItemsQuery.isLoading,
  ]);

  useEffect(() => {
    setShipping(JSON.parse(resolvedShippingKey));
  }, [resolvedShippingKey]);

  const handleTabNavigate = (target) => {
    routerNavigate(withMobileView(target));
  };

  const handleEdit = () => {
    routerNavigate(withMobileView("/myInfo"));
  };

  const openAddressEditor = () => {
    setShippingError("");
    setShippingSuccess("");
    setIsAddressEditorOpen(true);
  };

  const closeAddressEditor = () => {
    setIsAddressEditorOpen(false);
  };

  const saveAddressEditor = async (nextShippingInfo) => {
    const normalizedShippingInfo = normalizeShippingInfo(nextShippingInfo);
    setShippingError("");
    setShippingSuccess("");

    try {
      await shippingMutation.mutateAsync({
        recipient: normalizedShippingInfo.receiver,
        phone: normalizedShippingInfo.phone,
        postalCode: normalizedShippingInfo.postcode || "",
        address: normalizedShippingInfo.addressLine1,
        addressDetail: normalizedShippingInfo.addressLine2 || normalizedShippingInfo.detailAddress || "",
      });
      setShipping(normalizedShippingInfo);
      setShippingSuccess("배송지 변경 요청이 접수됐어요.");
      setIsAddressEditorOpen(false);
    } catch (error) {
      setShippingError(error?.message || "배송지 변경 요청을 접수하지 못했어요. 다시 시도해 주세요.");
    }
  };

  const handleLogoutToLogin = () => {
    logout({ redirectPath: "/?screen=login" });
  };

  const renderMyPageSection = (section) => {
    const sectionProps = getSectionProps(section);

    switch (section.key) {
      case "profile":
        return <ProfileCard key={section.key} {...sectionProps} user={user} onEdit={handleEdit} />;
      case "today_tasks":
        return (
          <TodayTasksSection
            key={section.key}
            {...sectionProps}
            items={actionSummary.items}
            isCountKnown={actionSummary.isKnown}
            onNavigate={handleTabNavigate}
          />
        );
      case "photos":
        return <MyPhotosSection key={section.key} {...sectionProps} onNavigate={handleTabNavigate} />;
      case "studio":
        return <StudioInfoSection key={section.key} {...sectionProps} studio={resolvedStudio} />;
      case "contract_payment":
        return (
          <ContractPaymentSection
            key={section.key}
            {...sectionProps}
            contract={resolvedContract}
          />
        );
      case "shipping":
        return (
          <ShippingInfoSection
            key={section.key}
            {...sectionProps}
            shipping={resolvedShipping}
            delivery={v4Profile?.delivery}
            onEdit={!v4Profile ? undefined : openAddressEditor}
          />
        );
      case "account_settings":
        return <AccountSettingsSection key={section.key} {...sectionProps} onLogout={handleLogoutToLogin} onNavigate={handleTabNavigate} />;
      default:
        return null;
    }
  };

  if (myPageConfig.page.enabled === false) {
    return (
      <ConfiguredPageDisabledState
        pageConfig={myPageConfig}
        activePath="/tabs/mypage"
        onNavigate={handleTabNavigate}
      />
    );
  }

  return (
    <section className="figma-mypage-page mobile-app-frame" data-node-id="6743:my-page">
      <MobileStatusBar />
      <MobileTopHeader />
      <main className="figma-mypage-scroll">
        <h1 className="figma-mypage-sr-only">{myPageConfig.page.title || TEXT.title}</h1>
        <ConfiguredPageIntro pageConfig={myPageConfig} />
        {myPageQuery.configured && myPageQuery.isLoading ? (
          <MystudioDataState type="loading" />
        ) : !myPageQuery.configured || myPageQuery.error ? (
          <MystudioDataState
            type="error"
            error={myPageQuery.error}
            title={!myPageQuery.configured ? "마이페이지 API가 연결되지 않았어요" : undefined}
            description={!myPageQuery.configured ? "MyStudio API 연결 주소를 확인해 주세요." : undefined}
            onRetry={myPageQuery.configured ? myPageQuery.refetch : undefined}
          />
        ) : (
          visibleMyPageSections.map(renderMyPageSection)
        )}
      </main>
      <MobileBottomTabBar activePath="/tabs/mypage" onNavigate={handleTabNavigate} />
      {shippingError ? <div className="figma-mypage-save-error" role="alert">{shippingError}</div> : null}
      {shippingSuccess ? <div className="figma-mypage-save-success" role="status">{shippingSuccess}</div> : null}
      {isAddressEditorOpen ? (
        <AddressChangeModal
          shippingInfo={shipping}
          onClose={closeAddressEditor}
          onSave={saveAddressEditor}
        />
      ) : null}
    </section>
  );
}
